import React, { useState, useEffect, useRef } from 'react';
import { loadGuardiaQuestions, getCorrectAnswerText } from '../utils/studyDataService';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

const ModoGuardia = () => {
  const { user, profileData } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedMateria, setSelectedMateria] = useState('ALL');
  const [materias, setMaterias] = useState([]);
  const [guardiaInfo, setGuardiaInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados de reproducción auditiva
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speechPhase, setSpeechPhase] = useState('IDLE'); // IDLE, QUESTION, PAUSE, ANSWER, POST_PAUSE
  const [pauseTime, setPauseTime] = useState(4); // segundos de pausa para pensar
  const [pauseCountdown, setPauseCountdown] = useState(0);
  const [rate, setRate] = useState(1.0); // Velocidad de voz
  const [voices, setVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState('');

  const timerRef = useRef(null);

  // Cargar preguntas conscientes del perfil del usuario (incluye sus conocimientos específicos)
  useEffect(() => {
    let cancelado = false;
    async function init() {
      setLoading(true);
      try {
        const resultado = await loadGuardiaQuestions(profileData, user?.uid);
        if (cancelado) return;
        const data = resultado.questions || [];
        setQuestions(data);

        const uniqueMats = Array.from(new Set(data.map(q => q.materia || 'General'))).filter(Boolean);
        setMaterias(uniqueMats);
        setFilteredQuestions(data);
        setGuardiaInfo(resultado);
        setSelectedMateria('ALL');
      } catch (e) {
        if (cancelado) return;
        console.error('Error cargando preguntas del Modo Guardia:', e);
        setQuestions([]);
        setFilteredQuestions([]);
        setMaterias([]);
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    init();
    return () => { cancelado = true; };
  }, [user?.uid]);

  // Cargar voces en español
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;

    const savedVoice = localStorage.getItem('modo_guardia_voice');

    const loadVoices = () => {
      const avail = window.speechSynthesis.getVoices();
      // Filtrar voces en español primeramente, si no hay, ofrecer todas las disponibles
      const esVoices = avail.filter(v => (v.lang || '').toLowerCase().startsWith('es'));
      const finalVoicesList = esVoices.length > 0 ? esVoices : avail;
      setVoices(finalVoicesList);

      if (savedVoice && finalVoicesList.some(v => v.voiceURI === savedVoice)) {
        setSelectedVoiceURI(savedVoice);
      } else if (finalVoicesList.length > 0 && !selectedVoiceURI) {
        setSelectedVoiceURI(finalVoicesList[0].voiceURI);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, [selectedVoiceURI]);

  // Filtrar por materia
  useEffect(() => {
    let list = [...questions];
    if (selectedMateria !== 'ALL') {
      list = list.filter(q => (q.materia || 'General') === selectedMateria);
    }
    setFilteredQuestions(list);
    setCurrentIndex(0);
    stopPlayback();
  }, [selectedMateria, questions]);

  const currentQ = filteredQuestions[currentIndex] || null;

  // Detener todo
  const stopPlayback = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsPlaying(false);
    setSpeechPhase('IDLE');
    setPauseCountdown(0);
  };

  // Función principal del ciclo de habla en Modo Guardia
  const playCurrentQuestionStep = (step = 'QUESTION') => {
    if (!currentQ || !('speechSynthesis' in window)) return;

    window.speechSynthesis.cancel();
    if (timerRef.current) clearInterval(timerRef.current);

    if (step === 'QUESTION') {
      setSpeechPhase('QUESTION');
      const text = `Pregunta número ${currentIndex + 1}. ${currentQ.pregunta}`;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'es-MX';
      utterance.rate = rate;

      if (selectedVoiceURI) {
        const selected = voices.find(v => v.voiceURI === selectedVoiceURI);
        if (selected) utterance.voice = selected;
      }

      utterance.onend = () => {
        // Pasar a PAUSA
        setSpeechPhase('PAUSE');
        setPauseCountdown(pauseTime);

        let countdown = pauseTime;
        timerRef.current = setInterval(() => {
          countdown -= 1;
          setPauseCountdown(countdown);

          if (countdown <= 0) {
            clearInterval(timerRef.current);
            playCurrentQuestionStep('ANSWER');
          }
        }, 1000);
      };

      utterance.onerror = () => stopPlayback();
      window.speechSynthesis.speak(utterance);
    } 
    else if (step === 'ANSWER') {
      setSpeechPhase('ANSWER');
      const ansText = getCorrectAnswerText(currentQ);
      const expText = currentQ.explicacion ? `. Fundamento: ${currentQ.explicacion}` : '';
      const fullText = `Respuesta correcta: ${ansText}${expText}`;

      const utterance = new SpeechSynthesisUtterance(fullText);
      utterance.lang = 'es-MX';
      utterance.rate = rate;

      if (selectedVoiceURI) {
        const selected = voices.find(v => v.voiceURI === selectedVoiceURI);
        if (selected) utterance.voice = selected;
      }

      utterance.onend = () => {
        // Pausa breve de 2 segundos antes de la siguiente pregunta
        setSpeechPhase('POST_PAUSE');
        timerRef.current = setTimeout(() => {
          // Avanzar a la siguiente
          setCurrentIndex(prev => {
            const nextIdx = prev + 1 < filteredQuestions.length ? prev + 1 : 0;
            return nextIdx;
          });
        }, 2000);
      };

      utterance.onerror = () => stopPlayback();
      window.speechSynthesis.speak(utterance);
    }
  };

  // Efecto para avanzar en la secuencia al cambiar currentIndex mientras isPlaying sea true
  useEffect(() => {
    if (isPlaying) {
      playCurrentQuestionStep('QUESTION');
    }
  }, [currentIndex]);

  const togglePlay = () => {
    if (isPlaying) {
      stopPlayback();
    } else {
      setIsPlaying(true);
      playCurrentQuestionStep('QUESTION');
    }
  };

  const handleNext = () => {
    stopPlayback();
    const nextIdx = currentIndex + 1 < filteredQuestions.length ? currentIndex + 1 : 0;
    setCurrentIndex(nextIdx);
  };

  const handlePrev = () => {
    stopPlayback();
    const prevIdx = currentIndex > 0 ? currentIndex - 1 : filteredQuestions.length - 1;
    setCurrentIndex(prevIdx);
  };

  const handleVoiceChange = (uri) => {
    setSelectedVoiceURI(uri);
    localStorage.setItem('modo_guardia_voice', uri);
  };

  const testVoice = () => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance("Atención. Probando selección de voz para el Modo Guardia.");
    utterance.lang = 'es-MX';
    utterance.rate = rate;
    if (selectedVoiceURI) {
      const selected = voices.find(v => v.voiceURI === selectedVoiceURI);
      if (selected) utterance.voice = selected;
    }
    window.speechSynthesis.speak(utterance);
  };

  if (loading) return <LoadingSpinner message="Cargando Módulo Modo Guardia..." />;

  if (filteredQuestions.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <p>No hay preguntas disponibles para los filtros seleccionados.</p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '1.5rem',
      color: 'var(--text-primary)',
      height: 'calc(100vh - 60px)',
      boxSizing: 'border-box',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    }}>
      {/* Header Modo Guardia */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderBottom: '1px solid rgba(197, 160, 89, 0.2)',
        paddingBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h1 style={{
            color: '#c5a059',
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '1.25rem',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            🛡️ Modo Guardia (Estudio Auditivo Pasivo)
          </h1>

          <span style={{
            fontSize: '0.75rem',
            padding: '4px 12px',
            borderRadius: '12px',
            background: isPlaying ? 'rgba(16, 185, 129, 0.2)' : 'rgba(197, 160, 89, 0.15)',
            color: isPlaying ? '#34d399' : '#c5a059',
            border: `1px solid ${isPlaying ? '#10b981' : '#c5a059'}`,
            fontWeight: 'bold',
            fontFamily: "'Orbitron', sans-serif"
          }}>
            {isPlaying ? '🔴 TRANSMITIENDO EN VIVO' : '⏸️ EN PAUSA'}
          </span>
        </div>

        <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(181, 200, 216, 0.8)' }}>
          Módulo de reproducción continua manos libres. Lee la pregunta, otorga una pausa de reflexión y recita la respuesta correcta con su fundamento legal. Ideal durante acondicionamiento físico o servicios.
        </p>

        {guardiaInfo && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            fontSize: '0.78rem',
            marginTop: '4px'
          }}>
            <span style={{
              padding: '4px 12px',
              borderRadius: '12px',
              background: guardiaInfo.filtradoPorPerfil ? 'rgba(16, 185, 129, 0.15)' : 'rgba(197, 160, 89, 0.12)',
              color: guardiaInfo.filtradoPorPerfil ? '#34d399' : '#c5a059',
              border: `1px solid ${guardiaInfo.filtradoPorPerfil ? '#10b981' : '#c5a059'}`
            }}>
              {guardiaInfo.filtradoPorPerfil
                ? `🎯 Perfil: ${guardiaInfo.jerarquiaNombre || guardiaInfo.jerarquiaKey} • ${guardiaInfo.especificosCount} específicos considerados`
                : '📚 Modo General (Sin perfil · todos los conocimientos)'}
            </span>
          </div>
        )}
      </div>

      {/* Selector de Materia y Configuración de Voz */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '12px',
        background: 'rgba(10, 26, 42, 0.8)',
        padding: '14px',
        borderRadius: '10px',
        border: '1px solid rgba(197, 160, 89, 0.2)'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', color: '#c5a059', fontFamily: "'Orbitron', sans-serif", marginBottom: '6px', textTransform: 'uppercase' }}>
            Materia / Ley:
          </label>
          <select
            value={selectedMateria}
            onChange={(e) => setSelectedMateria(e.target.value)}
            style={{
              width: '100%',
              background: '#071526',
              color: '#fff',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '0.8rem'
            }}
          >
            <option value="ALL">📚 Todas las Materias ({questions.length})</option>
            {materias.map((mat, idx) => (
              <option key={idx} value={mat}>{mat}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', color: '#c5a059', fontFamily: "'Orbitron', sans-serif", marginBottom: '6px', textTransform: 'uppercase' }}>
            Pausa de Reflexión:
          </label>
          <select
            value={pauseTime}
            onChange={(e) => setPauseTime(parseInt(e.target.value))}
            style={{
              width: '100%',
              background: '#071526',
              color: '#fff',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '0.8rem'
            }}
          >
            <option value={2}>⏱️ 2 Segundos (Rápido)</option>
            <option value={4}>⏱️ 4 Segundos (Estándar)</option>
            <option value={6}>⏱️ 6 Segundos (Extenso)</option>
            <option value={8}>⏱️ 8 Segundos (Profundo)</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', color: '#c5a059', fontFamily: "'Orbitron', sans-serif", marginBottom: '6px', textTransform: 'uppercase' }}>
            Velocidad Voz:
          </label>
          <select
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            style={{
              width: '100%',
              background: '#071526',
              color: '#fff',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '0.8rem'
            }}
          >
            <option value={0.85}>0.85x (Pausado)</option>
            <option value={1.0}>1.0x (Normal)</option>
            <option value={1.2}>1.2x (Ágil)</option>
            <option value={1.4}>1.4x (Rápido)</option>
          </select>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '0.7rem', color: '#c5a059', fontFamily: "'Orbitron', sans-serif", textTransform: 'uppercase' }}>
              Voz del Sintetizador:
            </label>
            <button
              onClick={testVoice}
              title="Probar voz seleccionada"
              style={{
                background: 'rgba(197, 160, 89, 0.15)',
                color: '#c5a059',
                border: '1px solid #c5a059',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '0.65rem',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🔊 Probar
            </button>
          </div>
          <select
            value={selectedVoiceURI}
            onChange={(e) => handleVoiceChange(e.target.value)}
            style={{
              width: '100%',
              background: '#071526',
              color: '#fff',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '0.8rem'
            }}
          >
            {voices.length > 0 ? (
              voices.map((v, idx) => (
                <option key={idx} value={v.voiceURI}>
                  🗣️ {v.name} ({v.lang})
                </option>
              ))
            ) : (
              <option value="">Voz Predeterminada del Sistema</option>
            )}
          </select>
        </div>
      </div>

      {/* Reproductor Central Táctico */}
      {currentQ ? (
        <div style={{
          background: 'linear-gradient(145deg, #0d2035 0%, #071526 100%)',
          border: '2px solid #c5a059',
          borderRadius: '16px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.7), 0 0 24px rgba(197,160,89,0.15)',
          position: 'relative'
        }}>
          {/* Radar Animado Tactical Visualizer */}
          <div style={{
            position: 'relative',
            width: '100px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              position: 'absolute',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              border: '2px solid rgba(197, 160, 89, 0.3)',
              boxShadow: isPlaying ? '0 0 20px rgba(197, 160, 89, 0.4)' : 'none',
              animation: isPlaying ? 'spin 4s linear infinite' : 'none'
            }} />
            <div style={{
              position: 'absolute',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              border: '1px dashed #c5a059',
              opacity: 0.6
            }} />
            <span style={{ fontSize: '2.5rem', zIndex: 2 }}>
              {speechPhase === 'QUESTION' ? '🗣️' : speechPhase === 'PAUSE' ? '🧠' : speechPhase === 'ANSWER' ? '💡' : '🛡️'}
            </span>
          </div>

          {/* Estado Actual del Audio */}
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '0.75rem',
              color: '#c5a059',
              fontFamily: "'Orbitron', sans-serif",
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {currentQ.materia || 'Legislación'} • Pregunta {currentIndex + 1} de {filteredQuestions.length}
            </span>

            <h2 style={{
              color: '#ffffff',
              fontSize: '1.15rem',
              marginTop: '10px',
              lineHeight: '1.5',
              maxWidth: '650px'
            }}>
              {currentQ.pregunta}
            </h2>
          </div>

          {/* Indicador Fase de Audio / Conteo */}
          {speechPhase === 'PAUSE' && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '10px 20px',
              color: '#fbbf24',
              fontFamily: "'Orbitron', sans-serif",
              fontSize: '0.85rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>⏱️ PAUSA DE REFLEXIÓN:</span>
              <span style={{ fontSize: '1.2rem', color: '#fff' }}>{pauseCountdown}s</span>
            </div>
          )}

          {speechPhase === 'ANSWER' && (
            <div style={{
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid #10b981',
              borderRadius: '8px',
              padding: '12px 18px',
              color: '#34d399',
              fontSize: '0.85rem',
              textAlign: 'center',
              maxWidth: '650px'
            }}>
              <strong>💡 Respuesta Correcta:</strong> {getCorrectAnswerText(currentQ)}
            </div>
          )}

          {/* Controles de Reproducción Auditiva */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginTop: '10px'
          }}>
            <button
              onClick={handlePrev}
              style={{
                background: 'rgba(10, 26, 42, 0.9)',
                border: '1px solid rgba(197, 160, 89, 0.4)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                color: '#c5a059',
                fontSize: '1.2rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Anterior Pregunta"
            >
              ⏮️
            </button>

            <button
              onClick={togglePlay}
              style={{
                background: isPlaying
                  ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
                  : 'linear-gradient(135deg, #c5a059 0%, #a88440 100%)',
                border: 'none',
                borderRadius: '50%',
                width: '68px',
                height: '68px',
                color: isPlaying ? '#fff' : '#071526',
                fontSize: '1.8rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isPlaying ? '0 0 20px rgba(239, 68, 68, 0.5)' : '0 0 20px rgba(197, 160, 89, 0.5)'
              }}
            >
              {isPlaying ? '⏸️' : '▶️'}
            </button>

            <button
              onClick={handleNext}
              style={{
                background: 'rgba(10, 26, 42, 0.9)',
                border: '1px solid rgba(197, 160, 89, 0.4)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                color: '#c5a059',
                fontSize: '1.2rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Siguiente Pregunta"
            >
              ⏭️
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ModoGuardia;
